import { PrimaryButton } from '../../components/Button'
import { usePostAppointmentContext } from '../../context/PostAppointmentContext/PostAppointmentProvider'
import Linkify from '../../components/Linkify'

/**
 * Component for displaying and editing the "note for patient" field of an appointment note.
 *
 * - An "appointment note" is the complete document that doctors create, containing multiple fields
 *   (tests ordered, follow-up timeline, referrals, etc.)
 * - The "note for patient" is just one field within that appointment note - it's a free-form
 *   text message/comment that the doctor writes specifically for the patient to read.
 *
 * Inline editing is only available for signed appointment notes. For unsaved/draft notes,
 * modifications should be done through the regular note creation flow. Meaning: providers
 * (doctors, dietitians...) should use the < Back link and/or save as draft and edit later).
 *
 * Notice that the goal of this component is not so much reusability but just cleaning
 * a bit the code in Review.tsx (it was getting a bit hard to follow)
 */
const NoteForPatient: React.FC = () => {
  const {
    selectedNote, // selectedNote is the complete appointment note document/object/thing
    // The specific "note for patient" field and its related state/handlers:
    noteForPatient,
    isEditingNoteForPatient,
    isSavingNoteForPatient,
    hasNoteForPatientChanged,
    updateNoteForPatient,
    startEditingNoteForPatient,
    cancelEditingNoteForPatient,
    saveNoteForPatient,
  } = usePostAppointmentContext()

  // Only allow editing for signed notes
  const isSignedNote = selectedNote?.status === 'signed'

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between">
        <p className="label mb-0">Note for patient:</p>
        {/* Edit button only shown for signed notes that aren't being edited */}
        {isSignedNote && !isEditingNoteForPatient && (
          <button
            className="text-action label mb-0 text-decoration-underline fw-normal text-uppercase"
            onClick={startEditingNoteForPatient}
            type="button"
          >
            Edit
          </button>
        )}
      </div>
      {isSignedNote && isEditingNoteForPatient ? (
        <>
          <textarea
            value={noteForPatient}
            onChange={updateNoteForPatient}
            className="form-control mt-2 fw-normal fs-sm"
            rows={5}
            aria-label="Note for patient text"
          />
          <p className="text-muted fst-italic mb-2 mt-1 small">
            Note: Modifying a signed note will not notify the patient of the
            update.
          </p>
          <div className="d-flex align-items-center justify-content-end mt-2">
            <button
              className="text-action label mb-0 text-decoration-underline fw-normal text-uppercase"
              onClick={cancelEditingNoteForPatient}
              type="button"
            >
              Cancel
            </button>
            <PrimaryButton
              className="ms-3 text-uppercase btn-sm"
              onClick={saveNoteForPatient}
              disabled={isSavingNoteForPatient || !hasNoteForPatientChanged}
            >
              {isSavingNoteForPatient ? 'Saving...' : 'Save'}
            </PrimaryButton>
          </div>
        </>
      ) : (
        <Linkify>
          <pre className="note-box fw-light font-family-roboto fs-4 p-3 m-0">
            <p className="m-0">{noteForPatient}</p>
          </pre>
        </Linkify>
      )}
    </div>
  )
}

export default NoteForPatient
